import PropTypes from 'prop-types'
import React, { Component } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { List } from 'immutable'
import { selectCurrentUserProfilePicture } from '../../services/community/identity/profile/redux/selectors'
import { get as getConfig } from '../../config/index'
import { getPrimary } from '../../services/languages/index'
import { DE, FR } from '../../services/languages/constants'
import { connect as connectStaticText } from '../StaticText/connect'
import { getBoundActions } from '../../actions/index'
import Button, { TYPES } from '../Button/index'
import AvatarUpload from '../AvatarUpload/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../Sherpa/index'
import { H2 } from '../Heading/index'

const dialogConfig = getConfig()
const communityProfileEnabled = !!dialogConfig?.features?.communityProfile

const DELETE_STATE = 'delete'
const DEFAULT_STATE = 'default'
const UPLOAD_STATE = 'update '

function getAvatarStyle (image) {
  const style = {}
  if (image) {
    style.backgroundImage = `url(${image})`
  }
  return style
}

function getBtnContainerCls (primaryLang) {
  const cls = ['dialog-update-profile-image__button-container']
  if (primaryLang === FR || primaryLang === DE) {
    cls.push('dialog-update-profile-image__button-container--lang')
  }
  return cls.join(' ')
}

const renderDefault = (props, onClickUpload, onClickDelete) => {
  const { staticText, user, currentUserPicture } = props
  const avatarImage = communityProfileEnabled && currentUserPicture
    ? currentUserPicture.url
    : user.getIn(['data', 'profile', 'picture', 'hdtv_190x266'])
  const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))
  return (
    <div className="dialog-update-profile-image">
      <H2 className="dialog-update-profile-image__title" fixed>{staticText.getIn(['data', 'title'])}</H2>
      <div className="dialog-update-profile-image__avatar" style={getAvatarStyle(avatarImage)} />
      <div className={getBtnContainerCls(primaryLang)}>
        <Button
          type={TYPES.SECONDARY}
          onClick={avatarImage ? onClickDelete : null}
          className="dialog-update-profile-image__button"
          stacked
        >
          {staticText.getIn(['data', 'delete'])}
        </Button>
        <Button
          type={TYPES.PRIMARY}
          onClick={onClickUpload}
          className="dialog-update-profile-image__button"
          stacked
        >
          {staticText.getIn(['data', 'upload'])}
        </Button>
      </div>
    </div>
  )
}

const renderDelete = (props, onClickDefault) => {
  const {
    staticText, auth, user, deleteAvatar, deleteUserProfileImages, communityProfile,
  } = props
  const processing = user.get('userProfileImagesProcessing')
    || (communityProfile && communityProfile.getIn(['avatarDelete', 'processing']))
  const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))

  return (
    <div className="dialog-update-profile-image">
      {
        processing
          ? <div className="dialog-update-profile-image__processing">
            <Sherpa className={['dialog-update-profile-image__sherpa']} type={TYPE_SMALL_BLUE} />
            <div className="dialog-update-profile-image__processing-text">
              {`${staticText.getIn(['data', 'deletingImage'])}...`}
            </div>
          </div>
          : null
      }
      <H2 className="dialog-update-profile-image__title dialog-update-profile-image__title--delete" fixed>
        {staticText.getIn(['data', 'sure'])}
      </H2>
      <div className={getBtnContainerCls(primaryLang)}>
        <Button
          type={TYPES.SECONDARY}
          onClick={() => {
            if (communityProfileEnabled) {
              deleteAvatar()
            } else {
              deleteUserProfileImages({ auth })
            }
          }}
          className="dialog-update-profile-image__button"
          stacked
        >
          {staticText.getIn(['data', 'delete'])}
        </Button>
        <Button
          type={TYPES.PRIMARY}
          onClick={onClickDefault}
          className="dialog-update-profile-image__button"
          stacked
        >
          {staticText.getIn(['data', 'cancel'])}
        </Button>
      </div>
    </div>
  )
}

const renderUpload = () => {
  return (
    <div className="dialog-update-profile-image">
      <AvatarUpload />
    </div>
  )
}

class DialogUpdateProfileImage extends Component {
  constructor (props) {
    super(props)
    this.state = {
      show: DEFAULT_STATE,
    }
  }

  componentDidUpdate (prevProps) {
    const { user: prevUser, communityProfile: prevCp } = prevProps
    const { user: nextUser, communityProfile: nextCp, setOverlayDialogVisible } = this.props

    // Close dialog on GraphQL avatar delete success
    const prevDeleteSuccess = prevCp && prevCp.getIn(['avatarDelete', 'success'])
    const nextDeleteSuccess = nextCp && nextCp.getIn(['avatarDelete', 'success'])
    if (nextDeleteSuccess && !prevDeleteSuccess) {
      setOverlayDialogVisible(null, false)
    }

    // Legacy fallback: close dialog on REST delete success
    const nextProfileImagesDeleteSuccess = nextUser.getIn(['deleteUserProfileImages', 'success'])
    const previousProfileImagesDeleteSuccess = prevUser.getIn(['deleteUserProfileImages', 'success'])
    if (nextProfileImagesDeleteSuccess !== previousProfileImagesDeleteSuccess) {
      setOverlayDialogVisible(null, false)
    }
  }

  componentWillUnmount () {
    const { clearUserProfileData } = this.props
    clearUserProfileData()
  }

  onClickDefault = () => {
    this.setState(() => ({ show: DEFAULT_STATE }))
  }

  onClickUpload = () => {
    this.setState(() => ({ show: UPLOAD_STATE }))
  }

  onClickDelete = () => {
    this.setState(() => ({ show: DELETE_STATE }))
  }

  render () {
    const { props } = this
    if (this.state.show === DEFAULT_STATE) {
      return renderDefault(props, this.onClickUpload, this.onClickDelete)
    } if (this.state.show === UPLOAD_STATE) {
      return renderUpload(props)
    } if (this.state.show === DELETE_STATE) {
      return renderDelete(props, this.onClickDefault)
    }
    return null
  }
}

DialogUpdateProfileImage.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  communityProfile: ImmutablePropTypes.map.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
  deleteAvatar: PropTypes.func.isRequired,
  deleteUserProfileImages: PropTypes.func.isRequired,
  clearUserProfileData: PropTypes.func.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'dialogUpdateProfileImage' }),
  connectRedux(
    (state) => ({
      currentUserPicture: selectCurrentUserProfilePicture(state),
      user: state.user,
      auth: state.auth,
      communityProfile: state.communityProfile,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        deleteAvatar: actions.communityProfile.deleteAvatar,
        deleteUserProfileImages: actions.user.deleteUserProfileImages,
        clearUserProfileData: actions.user.clearUserProfileData,
      }
    },
  ),
)(DialogUpdateProfileImage)
